import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, InputAdornment,
  IconButton, Avatar, Chip, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Skeleton,
  useTheme, alpha, Tabs, Tab, FormControl, InputLabel, Select,
  MenuItem, OutlinedInput, Checkbox, ListItemText, Divider,
  Paper, Tooltip, Collapse, LinearProgress, CircularProgress
} from '@mui/material';
import {
  Search, Person, Sports, Refresh, ViewList, ViewModule,
  ArrowUpward, ArrowDownward, Analytics,
  TrendingUp, EmojiEvents, Star, FilterList, Close
} from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';

import { playersApi } from '../services/apiService';
import { themeUtils } from '../theme/theme';
import { getCVRDisplay } from '../utils/cvrCalculations';
import { getTeamLogoUrl as getSharedTeamLogoUrl } from '../utils/teamLogos';
import { useYear } from '../context/YearContext';
import PageYearSelector from '../components/common/PageYearSelector';
import PerformanceTrend from '../components/charts/PerformanceTrend';
import useMobile from '../hooks/useMobile';
import { useSettings } from '../context/SettingsContext';

/* ═══════════════════════════════════════════════════════════════════════════
   Constants & Helpers
   ═══════════════════════════════════════════════════════════════════════════ */

const normalizeSearch = (str) =>
  (str || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();

/* ── Rank system (replaces letter grades) ────────────────────────────── */
// Maps stat dotted path → rankings key returned by backend
const STAT_TO_RANK_KEY = {
  'stats.batting.avg':  'avg',
  'stats.batting.obp':  'obp',
  'stats.batting.slg':  'slg',
  'stats.batting.ops':  'ops',
  'stats.batting.iso':  'iso',
  'stats.batting.wOBA': 'wOBA',
  'stats.pitching.era':               'era',
  'stats.pitching.whip':              'whip',
  'stats.pitching.fip':               'fip',
  'stats.pitching.strikeoutsPer9Inn': 'k9',
  'stats.pitching.walksPer9Inn':      'bb9',
};

// 6-tier color gradient based on rank percentile (green → red)
const getRankColor = (rank, total) => {
  if (!rank || !total) return '#9E9E9E';
  const pct = (rank - 1) / Math.max(total - 1, 1);
  if (pct <= 0.1)  return '#00C851';
  if (pct <= 0.25) return '#4CAF50';
  if (pct <= 0.45) return '#8BC34A';
  if (pct <= 0.65) return '#FFC107';
  if (pct <= 0.8)  return '#FF9800';
  return '#F44336';
};

// Get a player's ranking object for a stat key
const getPlayerRanking = (player, statKey) => {
  const rk = STAT_TO_RANK_KEY[statKey];
  return rk ? player.rankings?.[rk] : null;
};

// Inline rank badge component
const RankBadge = ({ ranking, label, size = 'small' }) => {
  if (!ranking) return null;
  const { rank, display, total } = ranking;
  const bg = getRankColor(rank, total);
  const dim = size === 'small' ? 26 : 32;
  return (
    <Tooltip title={`${label}: Rank ${display} of ${total}`} arrow>
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
        <Box sx={{ width: dim, height: dim, borderRadius: '50%', bgcolor: bg, display: 'flex',
          alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: dim === 26 ? '0.6rem' : '0.7rem', lineHeight: 1 }}>
            {display}
          </Typography>
        </Box>
        {label && (
          <Typography variant="caption" sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'text.secondary',
            textTransform: 'uppercase', lineHeight: 1 }}>{label}</Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const getTeamLogoUrl = (tc) => getSharedTeamLogoUrl(tc, 500);
const getNestedValue  = (o, p) => p.split('.').reduce((c, k) => c?.[k], o);
const makeSlug = (name) =>
  name.toLowerCase().replace(/\s+/g, '-').normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9-]/g, '');

const fmtSalary = (v) => {
  if (!v) return '\u2014';
  if (v >= 1e6) return '$' + (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return '$' + (v / 1e3).toFixed(0) + 'K';
  return '$' + v.toLocaleString();
};

// Savant-style heat-map: tint each cell based on rank within the current player pool
const buildHeatMap = (players, key, invert = false) => {
  const vals = players.map(p => getNestedValue(p, key)).filter(v => v != null && !isNaN(Number(v)));
  if (vals.length < 2) return {};
  const sorted = [...vals].map(Number).sort((a, b) => a - b);
  const pctOf = (v) => { const idx = sorted.findIndex(x => x >= Number(v)); return idx < 0 ? 1 : idx / (sorted.length - 1); };
  const result = {};
  for (const p of players) {
    const v = getNestedValue(p, key);
    if (v == null || isNaN(Number(v))) { result[p.name] = null; continue; }
    const pc = invert ? 1 - pctOf(v) : pctOf(v);
    let bg, fg;
    if      (pc >= 0.80) { bg = 'rgba(0,200,83,0.17)';   fg = '#00c853'; }
    else if (pc >= 0.60) { bg = 'rgba(124,179,66,0.12)'; fg = '#7cb342'; }
    else if (pc >= 0.40) { bg = 'transparent';           fg = 'inherit'; }
    else if (pc >= 0.20) { bg = 'rgba(239,108,0,0.11)';  fg = '#ef6c00'; }
    else                 { bg = 'rgba(198,40,40,0.14)';  fg = '#c62828'; }
    result[p.name] = { bg, fg };
  }
  return result;
};

// Stats where lower is better (invert heat-map)
const INVERT_STATS = new Set([
  'stats.pitching.era','stats.pitching.whip','stats.pitching.fip',
  'stats.pitching.walksPer9Inn','stats.pitching.hitsPer9Inn','stats.pitching.homeRunsPer9',
  'stats.pitching.runsScoredPer9','stats.pitching.pitchesPerInning','stats.pitching.pitchesPerBatter',
  'stats.batting.kRate','stats.batting.strikeOuts',
]);

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════════════ */

const Players = ({ category }) => {
  const { year } = useYear();
  const theme = useTheme();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const { defaultCategory, rowsPerPage: settingsRowsPerPage, densitySpacing } = useSettings();

  // ── Core data ──
  const [players, setPlayers]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError]               = useState(null);

  // ── Cross-year search fallback (when in-table filter finds nothing) ──
  const [crossYearResults, setCrossYearResults] = useState([]);

  // ── Hero search (cross-year via search-unified) ──
  const [heroQuery, setHeroQuery]       = useState('');
  const [heroResults, setHeroResults]   = useState([]);
  const [heroLoading, setHeroLoading]   = useState(false);
  const [heroOpen, setHeroOpen]         = useState(false);
  const heroRef = useRef(null);

  // ── Filters ──
  const [searchTerm, setSearchTerm]             = useState('');
  const [selectedTeams, setSelectedTeams]       = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [activeCategory, setActiveCategory]     = useState(category || defaultCategory || 'batting');
  const [sortBy, setSortBy]                     = useState('war');
  const [sortOrder, setSortOrder]               = useState('desc');
  const [dateRange, setDateRange]               = useState('all');
  const [gameType, setGameType]                 = useState('R');
  const [showAdvancedFilters, setShowAdvancedFilters]   = useState(false);

  // PA / IP filter (MLB-style qualifiers)
  const [minPA, setMinPA]                       = useState(0);
  const [minInnings, setMinInnings]             = useState(10);
  const [gamesIpFilterEnabled, setGamesIpFilterEnabled] = useState(true);

  // View
  const { isMobile } = useMobile();
  const [viewMode, setViewMode]                 = useState('table');

  // Auto-force table view on mobile (no card view on mobile)
  useEffect(() => { if (isMobile) setViewMode('cards'); }, [isMobile]);
  const [page, setPage]                         = useState(0);
  const [rowsPerPage, setRowsPerPage]           = useState(settingsRowsPerPage || 25);
  const [selectedStatGroup, setSelectedStatGroup] = useState('primary');

  // Derived lists
  const [teams, setTeams]         = useState([]);
  const [positions, setPositions] = useState([]);

  // Guard against out-of-order API responses: only the latest request may
  // write state. Slow uncached slices (date ranges, postseason) can resolve
  // AFTER a newer fast cached request and would otherwise overwrite the table
  // with stale data (e.g. last-14-days WAR shown under "Full Season").
  const loadSeqRef = useRef(0);

  /* ── Hero search handler (cross-year) ── */
  useEffect(() => {
    if (!heroQuery || heroQuery.length < 2) {
      setHeroResults([]); setHeroOpen(false); return;
    }
    setHeroLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await playersApi.searchUnified(heroQuery);
        setHeroResults(res.results || []);
        setHeroOpen(true);
      } catch { setHeroResults([]); }
      finally { setHeroLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [heroQuery]);

  // Close hero dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (heroRef.current && !heroRef.current.contains(e.target)) setHeroOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  /* ── Date range helpers ── */
  const getDateRangeParams = useCallback(() => {
    const now = new Date();
    switch (dateRange) {
      case 'last7':     return { startDate: format(subDays(now, 7),  'yyyy-MM-dd'), endDate: format(now, 'yyyy-MM-dd') };
      case 'last14':    return { startDate: format(subDays(now, 14), 'yyyy-MM-dd'), endDate: format(now, 'yyyy-MM-dd') };
      case 'last30':    return { startDate: format(subDays(now, 30), 'yyyy-MM-dd'), endDate: format(now, 'yyyy-MM-dd') };
      default:
        if (gameType === 'P') return { startDate: `${year}-10-01`, endDate: `${year}-11-15` };
        return null;
    }
  }, [dateRange, year, gameType]);

  const getDateRangeDescription = useCallback(() => {
    const labels = { last7: 'Last 7 Days', last14: 'Last 14 Days', last30: 'Last 30 Days' };
    return labels[dateRange] || 'Full Season';
  }, [dateRange]);

  /* ── Data loading ── */
  useEffect(() => { loadPlayers(); }, [activeCategory, year, gameType]);

  useEffect(() => {
    if (activeCategory === 'batting') {
      // MLB qualifier = 3.1 PA × team games. Default to ~25% of that for partial seasons.
      const maxG = Math.max(...players.map(p => p.gameCount || 0), 0);
      setMinPA(Math.max(10, Math.floor(maxG * 3.1 * 0.25)));
    } else { setMinInnings(10); }
  }, [activeCategory, players]);

  useEffect(() => {
    setPage(0);
    const t = setTimeout(() => {
      if (searchTerm || selectedTeams.length || selectedPosition !== 'all' || dateRange !== 'all') {
        setSearchLoading(true);
        loadPlayers().finally(() => setSearchLoading(false));
      } else loadPlayers();
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, selectedTeams, selectedPosition, dateRange, gameType]);

  const loadPlayers = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    try {
      if (!searchTerm && !selectedTeams.length && dateRange === 'all') setLoading(true);
      setError(null);
      const apiParams = { category: activeCategory, sortBy: 'war', sortOrder: 'desc', year, limit: 1000, slim: true, mode: 'rolling',
        ...(gameType && gameType !== 'all' && { gameType }) };
      if (selectedTeams.length === 1) apiParams.team = selectedTeams[0];
      const dp = getDateRangeParams();
      if (dp) { apiParams.startDate = dp.startDate; apiParams.endDate = dp.endDate; apiParams.dateRange = dateRange; }

      const res = await playersApi.getPlayers(apiParams);
      if (seq !== loadSeqRef.current) return; // stale response — a newer request superseded this one
      const pl = res.players || [];
      setPlayers(pl);
      if (pl.length) {
        setTeams([...new Set(pl.map(p => p.team))].sort());
        setPositions([...new Set(pl.map(p => p.position).filter(Boolean))].sort());
      }
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Error loading players:', err); setError(err.message);
    }
    finally { if (seq === loadSeqRef.current) setLoading(false); }
  }, [activeCategory, year, selectedTeams, searchTerm, dateRange, getDateRangeParams, gameType]);

  /* ── Filtering & sorting ── */
  const filteredPlayers = useMemo(() => {
    let list = [...players];
    if (searchTerm?.trim()) {
      const norm = normalizeSearch(searchTerm);
      const toks = norm.split(' ').filter(Boolean);
      list = list.filter(p => {
        const hay = `${normalizeSearch(p.name)} ${normalizeSearch(p.team)} ${normalizeSearch(p.position || '')}`;
        return toks.every(t => hay.includes(t));
      });
    }
    if (selectedTeams.length > 1) list = list.filter(p => selectedTeams.includes(p.team));
    if (selectedPosition !== 'all') list = list.filter(p => p.position === selectedPosition);

    if (sortBy) {
      list.sort((a, b) => {
        let aV = getNestedValue(a, sortBy), bV = getNestedValue(b, sortBy);
        if (aV == null && bV == null) return 0;
        if (aV == null) return 1;
        if (bV == null) return -1;
        const aN = typeof aV === 'string' ? parseFloat(aV) : aV;
        const bN = typeof bV === 'string' ? parseFloat(bV) : bV;
        let c = 0;
        if (typeof aN === 'number' && typeof bN === 'number' && !isNaN(aN) && !isNaN(bN)) c = aN - bN;
        else c = String(aV).localeCompare(String(bV));
        return sortOrder === 'desc' ? -c : c;
      });
    }
    return list;
  }, [players, searchTerm, selectedTeams, selectedPosition, sortBy, sortOrder]);

  const categoryFilteredPlayers = useMemo(() =>
    filteredPlayers.filter(p => {
      if (activeCategory === 'batting') {
        const b = p.stats?.batting;
        const pa = b?.plateAppearances ?? b?.atBats ?? 0;
        return b && (b.atBats > 0 || b.plateAppearances > 0) && (!gamesIpFilterEnabled || pa >= minPA);
      }
      const pi = p.stats?.pitching;
      const ip = parseFloat(pi?.inningsPitched) || 0;
      return pi && (ip > 0 || pi.gamesPlayed > 0) && (!gamesIpFilterEnabled || ip >= minInnings);
    }),
  [filteredPlayers, activeCategory, gamesIpFilterEnabled, minPA, minInnings]);

  const paginatedPlayers = useMemo(() =>
    categoryFilteredPlayers.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
  [categoryFilteredPlayers, page, rowsPerPage]);

  /* ── Cross-year fallback: when in-table filter finds 0, search all years ── */
  useEffect(() => {
    if (categoryFilteredPlayers.length === 0 && searchTerm?.trim()?.length >= 2) {
      const t = setTimeout(async () => {
        try {
          const res = await playersApi.searchUnified(searchTerm, { limit: 8 });
          setCrossYearResults(res.results || []);
        } catch { setCrossYearResults([]); }
      }, 400);
      return () => clearTimeout(t);
    } else {
      setCrossYearResults([]);
    }
  }, [categoryFilteredPlayers.length, searchTerm]);

  /* ── Tabs ── */
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (players.some(p => p.stats?.batting && (p.stats.batting.atBats > 0 || p.stats.batting.plateAppearances > 0)))
      tabs.push({ value: 'batting', label: 'Batting', icon: <Sports /> });
    if (players.some(p => p.stats?.pitching && (parseFloat(p.stats.pitching.inningsPitched) > 0 || p.stats.pitching.gamesPlayed > 0)))
      tabs.push({ value: 'pitching', label: 'Pitching', icon: <Person /> });
    return tabs;
  }, [players]);

  useEffect(() => {
    if (availableTabs.length && !availableTabs.find(t => t.value === activeCategory)) {
      setActiveCategory(availableTabs[0].value);
      setSortBy('war');
    }
  }, [availableTabs, activeCategory]);

  useEffect(() => { if (category && category !== activeCategory) setActiveCategory(category); }, [category]);
  useEffect(() => { setSelectedStatGroup('primary'); }, [activeCategory]);

  // Fix: preserve sort when switching categories if it is a universal key
  useEffect(() => {
    const universal = ['gameCount', 'war', 'cvr', 'acvr', 'position', 'salary.salary'];
    if (universal.includes(sortBy)) return;
    if (activeCategory === 'batting' && !sortBy.includes('batting')) setSortBy('war');
    else if (activeCategory === 'pitching' && !sortBy.includes('pitching')) setSortBy('war');
  }, [activeCategory]);

  /* ── Stat Configs ── */
  const statConfigs = {
    batting: [
      { key: 'stats.batting.avg', label: 'AVG', format: v => v?.toFixed(3) || '---' },
      { key: 'stats.batting.obp', label: 'OBP', format: v => v?.toFixed(3) || '---' },
      { key: 'stats.batting.slg', label: 'SLG', format: v => v?.toFixed(3) || '---' },
      { key: 'stats.batting.ops', label: 'OPS', format: v => v?.toFixed(3) || '---' },
      { key: 'stats.batting.homeRuns', label: 'HR', format: v => v || 0 },
      { key: 'stats.batting.iso', label: 'ISO', format: v => v?.toFixed(3) || '---' },
      { key: 'stats.batting.babip', label: 'BABIP', format: v => v?.toFixed(3) || '---' },
      { key: 'stats.batting.extraBaseHits', label: 'XBH', format: v => v || 0 },
      { key: 'stats.batting.extraBaseHitRate', label: 'XBH%', format: v => v ? (v * 100).toFixed(1) + '%' : '---' },
      { key: 'stats.batting.atBatsPerHomeRun', label: 'AB/HR', format: v => v?.toFixed(1) || '---' },
      { key: 'stats.batting.powerSpeed', label: 'PWR/SPD', format: v => v?.toFixed(1) || '---' },
      { key: 'stats.batting.kRate', label: 'K%', format: v => v ? (v * 100).toFixed(1) + '%' : '---' },
      { key: 'stats.batting.bbRate', label: 'BB%', format: v => v ? (v * 100).toFixed(1) + '%' : '---' },
      { key: 'stats.batting.contactRate', label: 'Contact%', format: v => v ? (v * 100).toFixed(1) + '%' : '---' },
      { key: 'stats.batting.walkToStrikeoutRatio', label: 'BB/K', format: v => v && v < 999 ? v.toFixed(2) : v > 0 ? '\u221E' : '---' },
      { key: 'stats.batting.stolenBases', label: 'SB', format: v => v || 0 },
      { key: 'stats.batting.stolenBasePercentage', label: 'SB%', format: v => v ? (v * 100).toFixed(1) + '%' : '---' },
      { key: 'stats.batting.atBats', label: 'AB', format: v => v || 0 },
      { key: 'stats.batting.rbi', label: 'RBI', format: v => v || 0 },
      { key: 'stats.batting.runs', label: 'R', format: v => v || 0 },
      { key: 'stats.batting.hits', label: 'H', format: v => v || 0 },
      { key: 'stats.batting.doubles', label: '2B', format: v => v || 0 },
      { key: 'stats.batting.triples', label: '3B', format: v => v || 0 },
      { key: 'stats.batting.plateAppearances', label: 'PA', format: v => v || 0 },
      { key: 'stats.batting.baseOnBalls', label: 'BB', format: v => v || 0 },
      { key: 'stats.batting.strikeOuts', label: 'K', format: v => v || 0 },
      { key: 'stats.batting.hitByPitch', label: 'HBP', format: v => v || 0 },
      { key: 'stats.batting.groundOutsToAirouts', label: 'GO/AO', format: v => v?.toFixed(2) || '---' },
      { key: 'salary.salary', label: 'Salary', format: fmtSalary },
    ],
    pitching: [
      { key: 'stats.pitching.inningsPitched', label: 'IP', format: v => v || '---' },
      { key: 'stats.pitching.era', label: 'ERA', format: v => v?.toFixed(2) || '---' },
      { key: 'stats.pitching.whip', label: 'WHIP', format: v => v?.toFixed(2) || '---' },
      { key: 'stuffPlus', label: 'Stuff+', format: v => v || '---' },
      { key: 'stats.pitching.winPercentage', label: 'WIN%', format: v => v ? (v * 100).toFixed(1) + '%' : '---' },
      { key: 'stats.pitching.fip', label: 'FIP', format: v => v?.toFixed(2) || '---' },
      { key: 'stats.pitching.xFip', label: 'xFIP', format: v => v?.toFixed(2) || '---' },
      { key: 'stats.pitching.babip', label: 'BABIP', format: v => v?.toFixed(3) || '---' },
      { key: 'stats.pitching.strandRate', label: 'LOB%', format: v => v ? (v * 100).toFixed(1) + '%' : '---' },
      { key: 'stats.pitching.strikeoutWalkRatio', label: 'K/BB', format: v => v && v < 999 ? v.toFixed(2) : v > 0 ? '\u221E' : '---' },
      { key: 'stats.pitching.strikeoutsPer9Inn', label: 'K/9', format: v => v?.toFixed(1) || '---' },
      { key: 'stats.pitching.walksPer9Inn', label: 'BB/9', format: v => v?.toFixed(1) || '---' },
      { key: 'stats.pitching.hitsPer9Inn', label: 'H/9', format: v => v?.toFixed(1) || '---' },
      { key: 'stats.pitching.homeRunsPer9', label: 'HR/9', format: v => v?.toFixed(1) || '---' },
      { key: 'stats.pitching.pitchesPerInning', label: 'P/IP', format: v => v?.toFixed(1) || '---' },
      { key: 'stats.pitching.pitchesPerBatter', label: 'P/BF', format: v => v?.toFixed(1) || '---' },
      { key: 'stats.pitching.strikePercentage', label: 'Strike%', format: v => v ? (v * 100).toFixed(1) + '%' : '---' },
      { key: 'stats.pitching.gameScore', label: 'GmScore', format: v => v?.toFixed(0) || '---' },
      { key: 'stats.pitching.qualityStart', label: 'QS', format: v => v || 0 },
      { key: 'stats.pitching.runsScoredPer9', label: 'R/9', format: v => v?.toFixed(2) || '---' },
      { key: 'stats.pitching.holds', label: 'HLD', format: v => v || 0 },
      { key: 'stats.pitching.saves', label: 'SV', format: v => v || 0 },
      { key: 'stats.pitching.blownSaves', label: 'BSV', format: v => v || 0 },
      { key: 'stats.pitching.savePercentage', label: 'SV%', format: v => v ? (v * 100).toFixed(1) + '%' : '---' },
      { key: 'stats.pitching.wins', label: 'W', format: v => v || 0 },
      { key: 'stats.pitching.losses', label: 'L', format: v => v || 0 },
      { key: 'stats.pitching.strikeOuts', label: 'K', format: v => v || 0 },
      { key: 'stats.pitching.baseOnBalls', label: 'BB', format: v => v || 0 },
      { key: 'stats.pitching.hits', label: 'H', format: v => v || 0 },
      { key: 'stats.pitching.earnedRuns', label: 'ER', format: v => v || 0 },
      { key: 'stats.pitching.homeRuns', label: 'HR', format: v => v || 0 },
      { key: 'stats.pitching.battersFaced', label: 'BF', format: v => v || 0 },
      { key: 'stats.pitching.groundOutsToAirouts', label: 'GO/AO', format: v => v?.toFixed(2) || '---' },
      { key: 'salary.salary', label: 'Salary', format: fmtSalary },
    ]
  };
  const currentStats = statConfigs[activeCategory] || [];

  /* ═══════════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════════ */

  if (loading) return <PlayersSkeleton />;

  if (error) return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography color="error" variant="h6">Error loading players: {error}</Typography>
      <Button startIcon={<Refresh />} onClick={loadPlayers} sx={{ mt: 2 }}>Retry</Button>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1600, mx: 'auto', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

      {/* ════════ Unified Search + Controls ════════ */}
      <Box sx={{ mb: 2 }}>
        {/* Row 1: Search bar + year selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box ref={heroRef} sx={{ position: 'relative', flex: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search players\u2026"
              value={heroQuery || searchTerm}
              onChange={e => {
                const v = e.target.value;
                setSearchTerm(v);
                setHeroQuery(v);
              }}
              onFocus={() => heroResults.length > 0 && setHeroOpen(true)}
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: 'text.secondary' }} /></InputAdornment>,
                endAdornment: heroLoading
                  ? <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
                  : (heroQuery || searchTerm)
                    ? <InputAdornment position="end"><IconButton size="small" onClick={() => { setHeroQuery(''); setSearchTerm(''); setHeroResults([]); setHeroOpen(false); }}><Close fontSize="small" /></IconButton></InputAdornment>
                    : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2, fontSize: '0.9rem',
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  transition: 'all 0.2s',
                  '&.Mui-focused': { boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.15)}` },
                }
              }}
            />

            {/* Cross-year results dropdown */}
            {heroOpen && heroResults.length > 0 && (
              <Paper elevation={8} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1200,
                mt: 0.5, borderRadius: 2, maxHeight: 360, overflow: 'auto', border: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ px: 2, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.6) }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>All Seasons</Typography>
                </Box>
                {heroResults.map((p, i) => (
                  <Box key={p.slug || i}
                    onClick={() => { navigate(`/players/profile/${p.slug || makeSlug(p.name)}`, { state: { prefill: p } }); setHeroOpen(false); setHeroQuery(''); setSearchTerm(''); }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, cursor: 'pointer',
                      borderBottom: i < heroResults.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
                      transition: 'background 0.15s', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) } }}>
                    <Avatar src={getTeamLogoUrl(p.currentTeam)} sx={{ width: 30, height: 30, bgcolor: themeUtils.getTeamColor(p.currentTeam) || '#1976d2' }}
                      imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>{p.currentTeam}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={700} variant="body2" noWrap>{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.position || '?'} &middot; {p.currentTeam}
                        {p.teams?.length > 0 && <> &middot; {p.teams.map(t => `${t.team} (${t.years?.join(', ')})`).join(' \u2022 ')}</>}
                      </Typography>
                    </Box>
                    <Chip label={p.playerType || '?'} size="small" variant="outlined" sx={{ textTransform: 'capitalize', height: 22, fontSize: '0.7rem' }} />
                  </Box>
                ))}
              </Paper>
            )}
          </Box>
          <PageYearSelector />
        </Box>

        {/* Row 2: Tabs + Season type + Sort + View controls — one clean line */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
          <Tabs value={activeCategory}
            onChange={(_, v) => { navigate(v === 'batting' ? '/players/batting' : v === 'pitching' ? '/players/pitching' : '/players', { replace: true }); setPage(0); }}
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600, py: 0.25, px: { xs: 1, sm: 1.5 }, minWidth: 'auto', fontSize: '0.8rem' }, '& .MuiTabs-indicator': { height: 2.5 } }}>
            {availableTabs.map(tab => <Tab key={tab.value} value={tab.value} label={tab.label} />)}
          </Tabs>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />

          {isMobile ? (
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select value={gameType} onChange={e => { setGameType(e.target.value); setPage(0); }}
                sx={{ fontSize: '0.75rem', fontWeight: 600, '& .MuiSelect-select': { py: 0.4 } }}>
                <MenuItem value="R">Regular</MenuItem>
                <MenuItem value="P">Post</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <ToggleButtonGroup value={gameType} exclusive
              onChange={(_, v) => { if (v) { setGameType(v); setPage(0); } }} size="small"
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.25, py: 0.25, fontSize: '0.75rem' } }}>
              <ToggleButton value="R">Regular</ToggleButton>
              <ToggleButton value="P">Post</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          )}

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select value={sortBy} onChange={e => setSortBy(e.target.value)}
              displayEmpty
              sx={{ fontSize: '0.75rem', fontWeight: 600, '& .MuiSelect-select': { py: 0.4 } }}>
              <MenuItem value="war">WAR</MenuItem>
              <MenuItem value="cvr">CVR</MenuItem>
              <MenuItem value="acvr">ACVR</MenuItem>
              <MenuItem value="gameCount">Games</MenuItem>
              <Divider />
              {currentStats.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>

          <Tooltip title={`Sort ${sortOrder === 'desc' ? 'Descending' : 'Ascending'}`}>
            <IconButton size="small" onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')} sx={{ p: 0.5 }}>
              {sortOrder === 'desc' ? <ArrowDownward sx={{ fontSize: 18 }} /> : <ArrowUpward sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: 0.5 }}>
              <Checkbox size="small" checked={gamesIpFilterEnabled} onChange={e => setGamesIpFilterEnabled(e.target.checked)} sx={{ p: 0.25 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                {activeCategory === 'batting' ? 'PA' : 'IP'}&ge;
              </Typography>
              <TextField type="number" size="small" variant="standard"
                value={activeCategory === 'batting' ? minPA : minInnings}
                onChange={e => { const v = Math.max(0, Number(e.target.value)); activeCategory === 'batting' ? setMinPA(v) : setMinInnings(v); }}
                disabled={!gamesIpFilterEnabled}
                sx={{ width: 36, '& .MuiInputBase-input': { textAlign: 'center', fontSize: '0.75rem', py: 0, px: 0 } }} />
            </Box>

            <Tooltip title="Table View"><IconButton size="small" onClick={() => setViewMode('table')} color={viewMode === 'table' ? 'primary' : 'default'} sx={{ p: 0.5 }}><ViewList sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            <Tooltip title="Card View"><IconButton size="small" onClick={() => setViewMode('cards')} color={viewMode === 'cards' ? 'primary' : 'default'} sx={{ p: 0.5 }}><ViewModule sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            <Tooltip title="Refresh"><IconButton size="small" onClick={loadPlayers} sx={{ p: 0.5 }}><Refresh sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            <Tooltip title={showAdvancedFilters ? 'Hide Filters' : 'Filters'}>
              <IconButton size="small" onClick={() => setShowAdvancedFilters(f => !f)} color={showAdvancedFilters ? 'primary' : 'default'} sx={{ p: 0.5 }}><FilterList sx={{ fontSize: 18 }} /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Collapsible advanced filters */}
        <Collapse in={showAdvancedFilters}>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Teams</InputLabel>
              <Select multiple value={selectedTeams} onChange={e => setSelectedTeams(e.target.value)}
                input={<OutlinedInput label="Teams" />}
                renderValue={sel => !sel.length ? 'All' : sel.length === 1 ? sel[0] : `${sel.length} teams`}
                sx={{ fontSize: '0.8rem' }}>
                {teams.map(t => <MenuItem key={t} value={t}><Checkbox size="small" checked={selectedTeams.includes(t)} /><ListItemText primary={t} /></MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Position</InputLabel>
              <Select value={selectedPosition} onChange={e => setSelectedPosition(e.target.value)} label="Position" sx={{ fontSize: '0.8rem' }}>
                <MenuItem value="all">All</MenuItem>
                {positions.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Date Range</InputLabel>
              <Select value={dateRange} onChange={e => setDateRange(e.target.value)} label="Date Range" sx={{ fontSize: '0.8rem' }}>
                <MenuItem value="all">Full Season</MenuItem>
                <MenuItem value="last7">Last 7 Days</MenuItem>
                <MenuItem value="last14">Last 14 Days</MenuItem>
                <MenuItem value="last30">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
            {(selectedTeams.length > 0 || selectedPosition !== 'all' || dateRange !== 'all') && (
              <Button size="small" variant="text" color="error" sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                onClick={() => { setSelectedTeams([]); setSelectedPosition('all'); setDateRange('all'); }}>
                Clear
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
              {categoryFilteredPlayers.length} players
              {dateRange !== 'all' && ` \u00B7 ${getDateRangeDescription()}`}
            </Typography>
          </Box>
        </Collapse>

        {searchLoading && <LinearProgress sx={{ mt: 0.5, borderRadius: 1 }} />}
      </Box>

      {/* ════════ Cross-year search results (when no local matches) ════════ */}
      {crossYearResults.length > 0 && categoryFilteredPlayers.length === 0 && (
        <Card elevation={0} sx={{ mb: 3, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`, borderRadius: 3,
          bgcolor: alpha(theme.palette.info.main, 0.04) }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} color="info.main" sx={{ mb: 1 }}>
              No results in {year} — found in other seasons:
            </Typography>
            {crossYearResults.map((p, i) => (
              <Box key={p.slug || i}
                onClick={() => navigate(`/players/profile/${p.slug || makeSlug(p.name)}`, { state: { prefill: p } })}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: { xs: 1.5, sm: 1.2 }, cursor: 'pointer',
                  borderRadius: 2, transition: 'background 0.15s',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) } }}>
                <Avatar src={getTeamLogoUrl(p.currentTeam)}
                  sx={{ width: 32, height: 32, bgcolor: themeUtils.getTeamColor(p.currentTeam) || '#1976d2' }}
                  imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>{p.currentTeam}</Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} variant="body2" noWrap>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.position || '?'} &middot; {p.teams?.map(t => `${t.team} (${t.years?.join(', ')})`).join(' \u2022 ')}
                  </Typography>
                </Box>
                <Chip label="View Profile" size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
              </Box>
            ))}
          </Box>
        </Card>
      )}

      {/* ════════ Main Data Display ════════ */}
      <AnimatePresence mode="wait">
        {viewMode === 'table' ? (
          <motion.div key="table" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>
            <PlayersTable
              players={paginatedPlayers} stats={currentStats}
              sortBy={sortBy} sortOrder={sortOrder}
              selectedStatGroup={selectedStatGroup} onStatGroupChange={setSelectedStatGroup}
              activeCategory={activeCategory}
              isMobile={isMobile}
              onSort={field => { if (field === sortBy) setSortOrder(o => o === 'desc' ? 'asc' : 'desc'); else { setSortBy(field); setSortOrder('desc'); } }}
              onPlayerClick={p => navigate(`/players/profile/${p.slug || makeSlug(p.name)}`, { state: { prefill: p } })}
            />
          </motion.div>
        ) : (
          <motion.div key="cards" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>
            <PlayersGrid players={paginatedPlayers} stats={currentStats}
              activeCategory={activeCategory}
              selectedStatGroup={selectedStatGroup}
              onStatGroupChange={setSelectedStatGroup}
              isMobile={isMobile}
              onPlayerClick={p => navigate(`/players/profile/${p.slug || makeSlug(p.name)}`, { state: { prefill: p } })} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pagination */}
      <Card elevation={0} sx={{ mt: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
        <TablePagination component="div" count={categoryFilteredPlayers.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={isMobile ? 'Rows:' : 'Rows per page:'}
          sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-end' }, minHeight: { xs: 48, sm: 52 } }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }} />
      </Card>


    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PlayersTable — WAR & CVR as fixed prominent columns
   ═══════════════════════════════════════════════════════════════════════════ */

const PlayersTable = ({ players, stats, sortBy, sortOrder, selectedStatGroup, onStatGroupChange, activeCategory, onSort, onPlayerClick, isMobile }) => {
  const theme = useTheme();

  const statGroups = activeCategory === 'batting' ? {
    primary: stats.slice(0, 8),
    power: stats.filter(s => ['stats.batting.homeRuns','stats.batting.iso','stats.batting.extraBaseHits','stats.batting.extraBaseHitRate','stats.batting.atBatsPerHomeRun','stats.batting.powerSpeed'].includes(s.key)),
    discipline: stats.filter(s => ['stats.batting.kRate','stats.batting.bbRate','stats.batting.contactRate','stats.batting.walkToStrikeoutRatio','stats.batting.babip'].includes(s.key)),
    counting: stats.filter(s => ['stats.batting.atBats','stats.batting.rbi','stats.batting.runs','stats.batting.hits','stats.batting.doubles','stats.batting.triples','stats.batting.plateAppearances','stats.batting.baseOnBalls','stats.batting.strikeOuts','stats.batting.hitByPitch','stats.batting.groundOutsToAirouts','stats.batting.stolenBases','stats.batting.stolenBasePercentage'].includes(s.key)),
  } : {
    primary: stats.slice(0, 8),
    strikeouts: stats.filter(s => ['stats.pitching.strikeoutWalkRatio','stats.pitching.strikeoutsPer9Inn','stats.pitching.walksPer9Inn','stats.pitching.hitsPer9Inn','stats.pitching.homeRunsPer9'].includes(s.key)),
    efficiency: stats.filter(s => ['stats.pitching.pitchesPerInning','stats.pitching.pitchesPerBatter','stats.pitching.strikePercentage','stats.pitching.gameScore','stats.pitching.qualityStart','stats.pitching.runsScoredPer9'].includes(s.key)),
    relief: stats.filter(s => ['stats.pitching.holds','stats.pitching.saves','stats.pitching.blownSaves','stats.pitching.savePercentage'].includes(s.key)),
    counting: stats.filter(s => ['stats.pitching.wins','stats.pitching.losses','stats.pitching.strikeOuts','stats.pitching.baseOnBalls','stats.pitching.hits','stats.pitching.earnedRuns','stats.pitching.homeRuns','stats.pitching.battersFaced','stats.pitching.groundOutsToAirouts'].includes(s.key)),
  };

  const visibleStats = isMobile
    ? (selectedStatGroup === 'all' ? stats : (statGroups[selectedStatGroup] || stats.slice(0, 4)))
    : selectedStatGroup === 'all' ? stats : (statGroups[selectedStatGroup] || stats.slice(0, 8));

  const SortHeader = ({ field, label, sx: sxProp = {} }) => {
    const active = sortBy === field;
    return (
      <TableCell align="center" sx={{ minWidth: { xs: 56, sm: 70 }, cursor: 'pointer', userSelect: 'none',
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
        ...(active && { bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }),
        ...sxProp }}
        onClick={() => onSort(field)}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>{label}</Typography>
          {active && (sortOrder === 'desc' ? <ArrowDownward sx={{ fontSize: '0.8rem' }} /> : <ArrowUpward sx={{ fontSize: '0.8rem' }} />)}
        </Box>
      </TableCell>
    );
  };

  // Heat-map percentile tints for every stat column
  const heatMaps = useMemo(() => {
    const keysToMap = [
      { key: 'war', invert: false },
      { key: 'cvr', invert: false },
      { key: 'acvr', invert: false },
      ...visibleStats.map(s => ({ key: s.key, invert: INVERT_STATS.has(s.key) })),
    ];
    const out = {};
    for (const { key, invert } of keysToMap) out[key] = buildHeatMap(players, key, invert);
    return out;
  }, [players, visibleStats]);

  return (
    <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.12)}`, borderRadius: 2, overflow: 'hidden' }}>
      {/* Stat-group chips */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, display: 'flex', gap: 0.5, flexWrap: { xs: 'nowrap', sm: 'wrap' }, alignItems: 'center', overflowX: { xs: 'auto', sm: 'visible' }, WebkitOverflowScrolling: 'touch', pb: { xs: 2, sm: 1.5 }, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: 'rgba(128,128,128,0.3)' } }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 1 }}>Stats:</Typography>
        {Object.keys(statGroups).map(gk => (
          <Chip key={gk} label={gk.charAt(0).toUpperCase() + gk.slice(1)} size="small"
            variant={selectedStatGroup === gk ? 'filled' : 'outlined'}
            color={selectedStatGroup === gk ? 'primary' : 'default'}
            onClick={() => onStatGroupChange(gk)}
            sx={{ cursor: 'pointer', textTransform: 'capitalize', fontWeight: 600, fontSize: '0.75rem' }} />
        ))}
        <Chip label="All" size="small"
          variant={selectedStatGroup === 'all' ? 'filled' : 'outlined'}
          color={selectedStatGroup === 'all' ? 'primary' : 'default'}
          onClick={() => onStatGroupChange('all')}
          sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }} />
      </Box>

      <TableContainer sx={{ maxHeight: '70vh', overflowX: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{
                position: 'sticky', left: 0, zIndex: 1100, width: { xs: 32, sm: 40 }, pl: { xs: 1, sm: 2 },
                fontSize: '0.72rem', fontWeight: 700, color: 'text.disabled', textAlign: 'center',
                bgcolor: alpha(theme.palette.background.paper, 0.97),
              }}>#</TableCell>
              <TableCell sx={{
                position: 'sticky', left: { xs: 32, sm: 40 }, zIndex: 1100, minWidth: { xs: 160, sm: 200 },
                fontWeight: 700, bgcolor: alpha(theme.palette.background.paper, 0.97),
                borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}>Player</TableCell>
              <SortHeader field="position" label="POS" sx={{ position: { xs: 'static', sm: 'sticky' }, left: { sm: 240 }, bgcolor: alpha(theme.palette.background.paper, 0.97), zIndex: { xs: 'auto', sm: 1100 }, minWidth: 55 }} />
              <SortHeader field="gameCount" label="G" />
              <SortHeader field="war" label="WAR" />
              {!isMobile && <SortHeader field="cvr" label="CVR" />}
              {!isMobile && <SortHeader field="acvr" label="ACVR" />}
              {visibleStats.map(s => <SortHeader key={s.key} field={s.key} label={s.label} />)}
            </TableRow>
          </TableHead>
          <TableBody>
            {players.map((player, idx) => {
              const warHeat  = heatMaps['war']?.[player.name];
              const cvrHeat  = heatMaps['cvr']?.[player.name];
              const acvrHeat = heatMaps['acvr']?.[player.name];

              return (
                <TableRow
                  key={`${player.name}-${idx}`}
                  hover
                  onClick={() => onPlayerClick(player)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                >
                  {/* Rank number */}
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1000, textAlign: 'center', px: 0.5, pl: { xs: 1, sm: 2 } }}>
                    <Typography variant="body2" color="text.disabled" fontWeight={700} sx={{ fontSize: '0.72rem' }}>{idx + 1}</Typography>
                  </TableCell>

                  {/* Player identity */}
                  <TableCell sx={{
                    position: 'sticky', left: { xs: 32, sm: 40 }, bgcolor: 'background.paper', zIndex: 1000,
                    borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar src={getTeamLogoUrl(player.team)}
                        sx={{ width: 28, height: 28, bgcolor: 'white', flexShrink: 0 }}
                        imgProps={{ style: { objectFit: 'contain' } }}>{player.team}</Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>{player.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{player.team} &middot; {player.position || '?'}</Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* POS */}
                  <TableCell align="center" sx={{ position: { xs: 'static', sm: 'sticky' }, left: { sm: 240 }, bgcolor: 'background.paper', zIndex: { xs: 'auto', sm: 1000 } }}>
                    <Chip label={player.position || '?'} size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 20, minWidth: 36, fontWeight: 600 }} />
                  </TableCell>

                  {/* G */}
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600 }}>{player.gameCount || 0}</Typography>
                  </TableCell>

                  {/* WAR — heat-map */}
                  <TableCell align="center" sx={{ px: 0.75, bgcolor: warHeat?.bg || 'transparent' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: warHeat?.fg || 'text.primary' }}>
                      {(player.war || 0).toFixed(1)}
                    </Typography>
                  </TableCell>

                  {/* CVR — heat-map */}
                  {!isMobile && (
                    <TableCell align="center" sx={{ px: 0.75, bgcolor: cvrHeat?.bg || 'transparent' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: cvrHeat?.fg || 'text.primary' }}>
                        {player.cvr != null ? Number(player.cvr).toFixed(1) : '\u2014'}
                      </Typography>
                    </TableCell>
                  )}

                  {/* ACVR — heat-map */}
                  {!isMobile && (
                    <TableCell align="center" sx={{ px: 0.75, bgcolor: acvrHeat?.bg || 'transparent' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: acvrHeat?.fg || 'text.primary' }}>
                        {player.acvr != null ? Number(player.acvr).toFixed(1) : '\u2014'}
                        {player.acvrTrend === 'up' ? ' \u2191' : player.acvrTrend === 'down' ? ' \u2193' : ''}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Per-stat cells — heat-map tinted */}
                  {visibleStats.map(stat => {
                    const heat = heatMaps[stat.key]?.[player.name];
                    const val  = getNestedValue(player, stat.key);
                    return (
                      <TableCell key={stat.key} align="center" sx={{ px: 0.75, bgcolor: heat?.bg || 'transparent' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: heat?.fg || 'text.primary' }}>
                          {stat.format(val)}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PlayersGrid — card view with WAR/CVR badges
   ═══════════════════════════════════════════════════════════════════════════ */

const PlayersGrid = ({ players, stats, onPlayerClick, activeCategory, selectedStatGroup, onStatGroupChange, isMobile }) => {
  const theme = useTheme();
  const isPitching = stats.some(s => s.key.includes('pitching'));

  const gradeStats = isPitching
    ? [{ key: 'stats.pitching.era', label: 'ERA', lower: true }, { key: 'stats.pitching.fip', label: 'FIP', lower: true }, { key: 'stats.pitching.whip', label: 'WHIP', lower: true }]
    : [{ key: 'stats.batting.avg', label: 'AVG' }, { key: 'stats.batting.ops', label: 'OPS' }, { key: 'stats.batting.wOBA', label: 'wOBA' }];

  // Build stat groups for chips (same as PlayersTable)
  const statGroups = activeCategory === 'batting' ? {
    primary: stats.slice(0, 8),
    power: stats.filter(s => ['stats.batting.homeRuns','stats.batting.iso','stats.batting.extraBaseHits','stats.batting.extraBaseHitRate','stats.batting.atBatsPerHomeRun','stats.batting.powerSpeed'].includes(s.key)),
    discipline: stats.filter(s => ['stats.batting.kRate','stats.batting.bbRate','stats.batting.contactRate','stats.batting.walkToStrikeoutRatio','stats.batting.babip'].includes(s.key)),
    counting: stats.filter(s => ['stats.batting.atBats','stats.batting.rbi','stats.batting.runs','stats.batting.hits','stats.batting.doubles','stats.batting.triples','stats.batting.plateAppearances','stats.batting.baseOnBalls','stats.batting.strikeOuts','stats.batting.hitByPitch','stats.batting.groundOutsToAirouts','stats.batting.stolenBases','stats.batting.stolenBasePercentage'].includes(s.key)),
  } : {
    primary: stats.slice(0, 8),
    strikeouts: stats.filter(s => ['stats.pitching.strikeoutWalkRatio','stats.pitching.strikeoutsPer9Inn','stats.pitching.walksPer9Inn','stats.pitching.hitsPer9Inn','stats.pitching.homeRunsPer9'].includes(s.key)),
    efficiency: stats.filter(s => ['stats.pitching.pitchesPerInning','stats.pitching.pitchesPerBatter','stats.pitching.strikePercentage','stats.pitching.gameScore','stats.pitching.qualityStart','stats.pitching.runsScoredPer9'].includes(s.key)),
    relief: stats.filter(s => ['stats.pitching.holds','stats.pitching.saves','stats.pitching.blownSaves','stats.pitching.savePercentage'].includes(s.key)),
    counting: stats.filter(s => ['stats.pitching.wins','stats.pitching.losses','stats.pitching.strikeOuts','stats.pitching.baseOnBalls','stats.pitching.hits','stats.pitching.earnedRuns','stats.pitching.homeRuns','stats.pitching.battersFaced','stats.pitching.groundOutsToAirouts'].includes(s.key)),
  };

  const visibleStats = isMobile
    ? (selectedStatGroup === 'all' ? stats : (statGroups[selectedStatGroup] || stats.slice(0, 8)))
    : stats.slice(0, 6);

  return (
    <>
      {/* Stat group chips for mobile */}
      {isMobile && onStatGroupChange && (
        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, mb: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, display: 'flex', gap: 0.5, flexWrap: 'nowrap', alignItems: 'center', overflowX: 'auto', WebkitOverflowScrolling: 'touch', '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: 'rgba(128,128,128,0.3)' } }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 1, flexShrink: 0 }}>Stats:</Typography>
            {Object.keys(statGroups).map(gk => (
              <Chip key={gk} label={gk.charAt(0).toUpperCase() + gk.slice(1)} size="small"
                variant={selectedStatGroup === gk ? 'filled' : 'outlined'}
                color={selectedStatGroup === gk ? 'primary' : 'default'}
                onClick={() => onStatGroupChange(gk)}
                sx={{ cursor: 'pointer', textTransform: 'capitalize', fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }} />
            ))}
            <Chip label="All" size="small"
              variant={selectedStatGroup === 'all' ? 'filled' : 'outlined'}
              color={selectedStatGroup === 'all' ? 'primary' : 'default'}
              onClick={() => onStatGroupChange('all')}
              sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }} />
          </Box>
        </Card>
      )}
      <Grid container spacing={isMobile ? 1.5 : 2}>
      {players.map((player, index) => {
        const teamColor  = themeUtils.getTeamColor(player.team) || theme.palette.primary.main;
        const warVal     = player.war || 0;
        const cvrDisplay = getCVRDisplay(player.cvr);

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`${player.name}-${index}`}>
            <motion.div whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }}>
              <Card elevation={0} sx={{ height: '100%', cursor: 'pointer',
                border: `1px solid ${theme.palette.divider}`, borderTop: `3px solid ${teamColor}`,
                borderRadius: 3, overflow: 'hidden', transition: 'all 0.2s',
                '&:hover': { borderColor: alpha(teamColor, 0.5), boxShadow: `0 6px 20px ${alpha(teamColor, 0.12)}` } }}
                onClick={() => onPlayerClick(player)}>
                <CardContent sx={{ p: 2 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Avatar src={getTeamLogoUrl(player.team)} sx={{ width: 40, height: 40, bgcolor: teamColor, mr: 1.5 }}
                      imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>{player.team}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>{player.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                        <Chip label={player.position || '?'} size="small"
                          sx={{ height: { xs: 22, sm: 18 }, fontSize: { xs: '0.75rem', sm: '0.75rem' }, fontWeight: 700, bgcolor: alpha(teamColor, 0.1), color: teamColor }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                          {player.team} &middot; {player.gameCount || 0}G
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* WAR + CVR badges */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <Box sx={{ flex: 1, textAlign: 'center', py: 0.8, borderRadius: 2,
                      bgcolor: alpha('#FFD700', 0.08), border: `1px solid ${alpha('#FFD700', 0.2)}` }}>
                      <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>WAR</Typography>
                      <Typography variant="body2" fontWeight={800}
                        sx={{ fontSize: '0.95rem', color: warVal >= 6 ? '#4CAF50' : warVal >= 2 ? '#FFC107' : '#F44336' }}>
                        {warVal.toFixed(1)}
                      </Typography>
                    </Box>
                    {player.cvr > 0 && (
                      <Box sx={{ flex: 1, textAlign: 'center', py: 0.8, borderRadius: 2,
                        bgcolor: alpha('#FF6F00', 0.08), border: `1px solid ${alpha('#FF6F00', 0.2)}` }}>
                        <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>CVR</Typography>
                        <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.95rem' }}>
                          {cvrDisplay.value}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Rank badges */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 1.5, py: 0.8,
                    bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 2 }}>
                    {gradeStats.map(gs => {
                      const ranking = getPlayerRanking(player, gs.key);
                      if (!ranking) return null;
                      return <RankBadge key={gs.key} ranking={ranking} label={gs.label} size="small" />;
                    })}
                  </Box>

                  {/* Key stats */}
                  <Grid container spacing={0.5}>
                    {visibleStats.map(stat => {
                      const val = getNestedValue(player, stat.key);
                      const ranking = getPlayerRanking(player, stat.key);
                      const dotColor = ranking ? getRankColor(ranking.rank, ranking.total) : null;
                      return (
                        <Grid item xs={4} key={stat.key}>
                          <Box sx={{ textAlign: 'center', py: { xs: 0.6, sm: 0.4 } }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.75rem' }, fontWeight: 600, textTransform: 'uppercase' }}>
                              {stat.label}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                              {dotColor && (
                                <Tooltip title={`Rank ${ranking.display} of ${ranking.total}`} arrow>
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor }} />
                                </Tooltip>
                              )}
                              <Typography variant="body2" fontWeight={700}
                                sx={{ fontSize: '0.82rem', color: themeUtils.getStatColor(val, stat.key.split('.').pop(), theme) }}>
                                {stat.format(val)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        );
      })}
    </Grid>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Skeleton loader
   ═══════════════════════════════════════════════════════════════════════════ */

const PlayersSkeleton = () => (
  <Box sx={{ p: 3 }}>
    <Skeleton variant="text" width={200} height={50} sx={{ mb: 1 }} />
    <Skeleton variant="rectangular" height={56} sx={{ mb: 3, borderRadius: 3 }} />
    <Skeleton variant="rectangular" height={120} sx={{ mb: 3, borderRadius: 3 }} />
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {[1, 2, 3, 4].map(i => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>
    <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
  </Box>
);

export default Players;
